import { AlertCircle, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiError, sourcesApi, type GovernedSource } from "../../services/api";
import { selectionFor } from "../../services/dataDomain";
import { devTrace } from "../../services/devTrace";
import type { SelectedContext } from "../../services/storage";
import { projectTypeLabel } from "../assistants/assistants";

/**
 * Chooses which governed context the AI Analyst answers from.
 *
 * The page used to collect raw warehouse credentials, which was misleading: the
 * analyst never queries a database directly, it queries a published context, and
 * the connection behind that context is owned by the Business Context Layer. What a
 * user actually needs to decide here is which certified contract to work from.
 */
interface DataSourcesPageProps {
  selectedContext: SelectedContext | null;
  onSelectContext: (context: SelectedContext) => void;
}

export function DataSourcesPage({
  selectedContext,
  onSelectContext,
}: DataSourcesPageProps) {
  // null while the context layer is being checked, so the page can tell
  // "still loading" apart from "nothing published".
  const [governed, setGoverned] = useState<GovernedSource[] | null>(null);
  const [governedError, setGovernedError] = useState("");

  useEffect(() => {
    let cancelled = false;
    sourcesApi
      .list()
      .then((items) => {
        if (!cancelled) setGoverned(items);
      })
      .catch((error) => {
        if (cancelled) return;
        setGoverned([]);
        setGovernedError(
          error instanceof ApiError
            ? error.message
            : "The context layer could not be reached.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function chooseContext(source: GovernedSource) {
    const chosen = selectionFor(source);
    // App owns the selection so the already-mounted analyst receives it in the
    // same render. App also persists it so the choice survives a reload.
    onSelectContext(chosen);
    devTrace.record({
      runId: crypto.randomUUID(),
      step: "context_selected",
      actor: "Data Sources UI",
      level: "success",
      summary: `Analysis pinned to ${source.name} ${source.contextVersion}.`,
      detail: {
        contextId: source.id,
        contextVersion: source.contextVersion,
        sourceSystem: source.sourceSystem,
      },
    });
  }

  const queryable = governed?.filter((source) => source.queryable) ?? [];
  const totalMetrics = queryable.reduce(
    (total, item) => total + item.metricCount,
    0,
  );
  // Once a context is chosen the tiles describe that context, because it is the
  // scope every answer will come from. Before a choice they describe the
  // catalog, which is the only meaningful reading at that point.
  const selected =
    governed?.find((source) => source.id === selectedContext?.contextId) ?? null;

  return (
    <div className="content-stack">
      <section className="hero compact-hero">
        <div>
          <h1>Semantic Model Inventory</h1>
          <p>
            Every published semantic model, what it covers, and whether it can
            answer a question right now.
          </p>
        </div>
        <div className="hero-stats">
          <div>
            <strong>
              {governed === null
                ? "…"
                : selected
                  ? selected.entityCount
                  : queryable.length}
            </strong>
            <small>{selected ? "Entities in scope" : "Queryable contexts"}</small>
          </div>
          <div>
            <strong>
              {governed === null
                ? "…"
                : selected
                  ? selected.metricCount
                  : totalMetrics}
            </strong>
            <small>Certified metrics</small>
          </div>
          <div>
            <strong>{selectedContext ? selectedContext.name : "None"}</strong>
            <small>{selectedContext ? "Active selection" : "Nothing selected"}</small>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Published semantic models</h2>
            <p>
              Models the AI Analyst can query right now, reported by the
              Business Context Layer.
            </p>
          </div>
          {selectedContext && (
            <span className="status-badge certified">
              <Check size={15} aria-hidden="true" />
              {selectedContext.name} {selectedContext.contextVersion}
            </span>
          )}
        </div>
        {governed === null && !governedError && (
          <p className="muted">Checking the context layer…</p>
        )}
        {governedError && (
          <div className="form-error" role="alert">
            <AlertCircle size={17} aria-hidden="true" />
            {governedError}
          </div>
        )}
        {governed?.length === 0 && !governedError && (
          <p className="muted">
            No published context is available, so the analyst has nothing to
            query yet.
          </p>
        )}
        {governed && governed.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Use</th>
                <th scope="col">Semantic model</th>
                <th scope="col">Created by</th>
                <th scope="col">Project type</th>
                <th scope="col">Description</th>
                <th scope="col">Source system</th>
                <th scope="col">Version</th>
                <th scope="col" className="numeric">Entities</th>
                <th scope="col" className="numeric">Metrics</th>
                <th scope="col" className="numeric">Rules</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {governed.map((source) => (
                <tr key={source.id}>
                  <td>
                    <input
                      type="radio"
                      name="active-context"
                      value={source.id}
                      checked={selectedContext?.contextId === source.id}
                      // A context with no runtime cannot answer anything, so it
                      // is shown for transparency but cannot be chosen.
                      disabled={!source.queryable}
                      onChange={() => chooseContext(source)}
                      aria-label={`Use ${source.name} for analysis`}
                    />
                  </td>
                  <td>
                    <strong>{source.name}</strong>
                    <small className="muted"> {source.id}</small>
                  </td>
                  <td>
                    {source.createdBy || <span className="muted">Unattributed</span>}
                  </td>
                  <td>
                    {source.dataDomain ? (
                      projectTypeLabel(source.dataDomain)
                    ) : (
                      <span
                        className="muted"
                        title="Nobody has classified this context yet, so it is grouped under Unassigned"
                      >
                        Unassigned
                      </span>
                    )}
                  </td>
                  <td>{source.description || <span className="muted">—</span>}</td>
                  <td>
                    {source.sourceSystem || <span className="muted">unknown</span>}
                    {source.catalogs.length > 0 && (
                      <small className="muted">
                        {" "}
                        {source.catalogs.join(", ")}
                        {source.schemas.length > 0 ? ` · ${source.schemas.join(", ")}` : ""}
                      </small>
                    )}
                  </td>
                  <td>
                    {source.contextVersion}
                    {source.availableVersions.length > 1 && (
                      <small className="muted"> of {source.availableVersions.length}</small>
                    )}
                  </td>
                  <td className="numeric">{source.entityCount}</td>
                  <td className="numeric">{source.metricCount}</td>
                  <td className="numeric">{source.ruleCount}</td>
                  <td>
                    <span
                      className={`status-pill ${
                        source.status === "connected" ? "ready" : "warn"
                      }`}
                    >
                      {source.status === "connected"
                        ? "Connected"
                        : source.queryable
                          ? "Unavailable"
                          : "Not queryable"}
                    </span>
                    {!source.queryable && (
                      <small className="muted"> no runtime bound</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
